import React from 'react';
import withShipment from '../../../../withShipment';
import PropTypes from 'prop-types';
import './TopToolbar.css';

import { IconButton, Toolbar, Tooltip, Typography } from '@material-ui/core';
import { Popper } from '@mui/material';
import {
  userInfoSelector,
  viewOnlySelector,
} from '../../../../redux/selectors/auth';
import Utils from '../../../Utils/Utils';
import { filterToolbarItems } from '../../../../constants/readOnlyAccess';
import scrollAppToTop from '../../../Utils/scrollToTop';

class TopToolbar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activePopper: null, // Change to track the identifier of the active popper
      anchorEl: null,
    };
  }

  componentDidMount() {
    // Opening a page (or a sub-screen rendered by a different component,
    // which mounts its own toolbar) starts at the top.
    scrollAppToTop();
  }

  componentDidUpdate(
    prevProps: Readonly<P>,
    prevState: Readonly<S>,
    snapshot: SS
  ) {
    if (prevProps.menuItems !== this.props.menuItems) {
      this.setState({ activePopper: null });
    }
    // Every page in the app renders this toolbar, and a page that swaps a
    // sub-screen in place (Boost's screens, Customer list -> profile ->
    // form, Machine Picture's add view, the loan/Zelle report detail
    // views) re-titles it. That makes the title the one global signal for
    // "the content under this toolbar just changed" on routes that never
    // change. Without it the new screen opened at the offset the previous
    // one was scrolled to — landing you halfway down a page you just
    // opened. Titles here are static per screen, never interpolated with
    // counts, so this can't fire on a data refresh.
    if (prevProps.pageName !== this.props.pageName) {
      scrollAppToTop();
    }
  }

  handleClick = (event, menuItem, index) => {
    // Use index or a unique identifier from menuItem as the key
    if (menuItem.popper) {
      this.setState({
        activePopper: this.state.activePopper === index ? null : index,
        anchorEl: event.currentTarget, // Update this line
      });
    } else {
      menuItem.onClick();
    }
  };

  handleClosePopper = () => {
    this.setState({ activePopper: null });
  };

  displayMenuItems = (rawMenuItems) => {
    if (!rawMenuItems) return null;

    // Central read-only enforcement for every page that renders this
    // toolbar: at a VIEW-ONLY location the write actions are removed
    // outright rather than shown disabled, so a viewer is never invited
    // into an add/edit flow that can't be saved. Read actions (Back,
    // Download PDF, Print, Zoom, Refresh, View Report) are untouched.
    // See constants/readOnlyAccess.js for the classification.
    const menuItems = filterToolbarItems(rawMenuItems, this.props.viewOnly);

    return menuItems.map((menuItem, index) => {
      const isOpen = this.state.activePopper === index; // Check if this popper is active
      if (menuItem.isNotIcon) {
        return (
          <div key={menuItem.title || index} className="menu-item-title">
            {menuItem.title}
          </div>
        );
      }
      return (
        <React.Fragment key={menuItem.title || index}>
          <Tooltip title={menuItem.title}>
            <IconButton
              aria-label={menuItem.title}
              onClick={(event) => this.handleClick(event, menuItem, index)} // Pass index or unique ID
              disabled={menuItem.disabled}
            >
              {menuItem.icon}
            </IconButton>
          </Tooltip>
          {menuItem.popper && (
            <Popper
              open={isOpen}
              anchorEl={isOpen ? this.state.anchorEl : null}
              placement={'bottom-start'}
              style={{ zIndex: '100' }}
            >
              {menuItem.popper &&
                React.cloneElement(menuItem.popper, {
                  handleClosePopper: this.handleClosePopper,
                })}
            </Popper>
          )}
        </React.Fragment>
      );
    });
  };

  render() {
    return (
      <React.Fragment>
        <div
          className="topToolbar"
          style={{
            backgroundColor: Utils.isValidColor(
              this.props.userInfo?.employeeColor
            )
              ? this.props.userInfo?.employeeColor
              : '#7896ff',
          }}
        >
          <Toolbar>
            {this.displayMenuItems(this.props.menuItems)}
            <Typography
              className="titleTypography"
              variant="h6"
              id="tableTitle"
            >
              {this.props.pageName}
            </Typography>
          </Toolbar>
        </div>
      </React.Fragment>
    );
  }
}

TopToolbar.defaultProps = {
  pageName: 'page name',
};

TopToolbar.propTypes = {
  pageName: PropTypes.string,
};

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  viewOnly: viewOnlySelector(state),
});

const actionCreators = {};

export default withShipment(
  {
    mapStateToProps,
    actionCreators,
  },
  TopToolbar
);
